import { DecimalPipe, NgTemplateOutlet } from "@angular/common";
import {
  Component,
  Input,
  inject,
  input,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  output,
  type SimpleChanges,
  type TemplateRef,
  viewChild,
} from "@angular/core";
import { type PageEvent, Paginator, ProgressSpinner, TooltipDirective } from "@ui";
import type { Subscription } from "rxjs";
import { AppConfig } from "../../app-config";
import type { ColumnMeta } from "@pienso/ingest-types/corpus.interface";
import { ES_TYPE } from "../services/elastic-search.service";
import { CellRendererDirective } from "./cell-renderer.directive";
import { DefaultGridRendererProvider } from "./default-grid-renderer-provider";
import { Page } from "./grid-data.provider";
import type { ColumnInfo, GridDataService } from "./grid-data-service.interface";
import type { GridRendererProvider } from "./grid-renderer-provider.interface";
import { HeaderRendererDirective } from "./header-renderer.directive";

export interface ColumnTypes {
  [colId: string]: {
    type: ES_TYPE;
    format?: string; // used for date format
  };
}

@Component({
  selector: "app-data-grid",
  templateUrl: "./data-grid.component.html",
  styleUrls: ["./data-grid.component.scss"],
  imports: [
    ProgressSpinner,
    TooltipDirective,
    HeaderRendererDirective,
    CellRendererDirective,
    Paginator,
    DecimalPipe,
    NgTemplateOutlet,
  ],
})
export class DataGridComponent implements OnInit, OnChanges, OnDestroy {
  private appConfig = inject(AppConfig);

  static decimalPipe: DecimalPipe;

  readonly dataService = input.required<GridDataService>();

  private subscriptions: Subscription[] = [];

  @Input() rendererProvider: GridRendererProvider = new DefaultGridRendererProvider();

  readonly editable = input(true); // TODO: eliminate?

  readonly externalTransforming = input<boolean>(false); // ability for parent component to notify that there is an ongoing corpus transformation

  readonly columnEdit = output<ColumnMeta>(); // TODO: Move to transform?

  readonly columnTypeEdit = output<ColumnTypes>(); // TODO: Move to transform

  page: Page = new Page();
  columns: any[] | null = null;
  idColumn: string | null = null;
  // True while dataService is fetching a corpus/page/column-type reload; the grid keeps
  // rendering its last-known page/columns underneath the loading overlay while this is true.
  dataReloading = false;

  readonly isLoading = input(false);

  tableMessages = {
    emptyMessage: "No data to display",
    totalMessage: "total",
  };

  readonly headerTemplate = viewChild<TemplateRef<any>>("headerTpl");
  readonly rowNumHeaderTemplate = viewChild<TemplateRef<any>>("rowNumHdrTpl");
  readonly rowNumCellTemplate = viewChild<TemplateRef<any>>("rowNumCellTpl");
  readonly cellTemplate = viewChild<TemplateRef<any>>("cellTpl");

  readonly dateModalTemplate = viewChild<TemplateRef<any>>("dateModalTpl");

  constructor() {
    const appConfig = this.appConfig;

    if (!DataGridComponent.decimalPipe) {
      DataGridComponent.decimalPipe = new DecimalPipe(appConfig.locale);
    }
  }

  async ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["dataService"]) {
      this.unsubscribe();
      const dataService = this.dataService();
      if (dataService) {
        this.subscriptions.push(
          dataService.gridData$.subscribe((data) => {
            this.updateColumns(data ? data.columnInfo : null);
            this.setPage(data?.page ?? new Page());
          }),
          dataService.isLoading$.subscribe((loading) => {
            this.dataReloading = loading;
          }),
        );
      }
    }
    // ensure a renderer provider exists
    if (!this.rendererProvider && !changes["rendererProvider"]) {
      this.rendererProvider = new DefaultGridRendererProvider();
    }
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  private unsubscribe() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  private updateColumns(info: ColumnInfo | null) {
    if (!info) {
      this.idColumn = this.columns = null;
      return;
    }

    const meta = info.columnMeta;
    const types = info.columnTypes;
    this.idColumn = meta.idColumn;
    this.columns = [
      {
        prop: "__rowNumber__",
        name: "",
        headerTemplate: this.rowNumHeaderTemplate(),
        cellTemplate: this.rowNumCellTemplate(),
        frozenLeft: true,
        draggable: false,
        sortable: false,
        width: 40,
        dataType: { type: ES_TYPE.LONG },
      },
    ];
    meta.columnOrder.forEach((prop) => {
      this.columns!.push({
        prop,
        name: meta.columns[prop].name,
        headerTemplate: this.headerTemplate(),
        sortable: false, // TODO: support sorting!
        cellTemplate: this.cellTemplate(),
        dataType: types[prop],
      });
    });
  }

  private prevPageRequestTime = 0; // The grid seems to have a bug and requests page 0 immediately after requesting another page
  requestPage(pageNumber: number) {
    const t = Date.now();
    if (t - this.prevPageRequestTime > 200) {
      this.dataService().loadPage(pageNumber);
      this.prevPageRequestTime = t;
    }
  }

  /**
   * Populate the table with new data based on the page number.
   */
  setPage(page: Page) {
    this.page = page;

    let totalMsg = "total";
    if (page.totalRows > page.availableRows) {
      totalMsg = `shown of ${DataGridComponent.decimalPipe.transform(page.totalRows, "1.0-0")}`;
    }
    this.tableMessages.totalMessage = totalMsg;
  }

  getGridTemplate(): string {
    return `[start] 4rem repeat(${Math.max((this.columns?.length ?? 1) - 1, 0)}, minmax(12rem, 1fr)) [end]`;
  }

  onPageChange(event: PageEvent): void {
    const dataService = this.dataService();
    if (event.pageSize !== dataService.pageSize) {
      dataService.setPageSize(event.pageSize);
    } else {
      this.requestPage(event.pageIndex);
    }
  }
}
